import type { QueryKey } from '@tanstack/react-query';
import { CART_COUNT_STALE_TIME_MS } from './cartCount';

/**
 * The header mini-cart's commerce reads, kept in localStorage for a minute
 * (CPS-7696). Product cards open every PDP in a new tab, and each tab would
 * otherwise read the cart, its products and the ownership checks again just
 * to draw the badge.
 *
 * An entry belongs to one user in one org. Invalidating it leaves a fence
 * (`clearedAt`) behind: a read that started before the fence may hold what
 * the change replaced, so no tab may store it afterwards. Invalidating and
 * clearing also raise one fence for every identity, under a key no write
 * touches, so a write racing them in another tab, or for an identity with
 * nothing stored yet, cannot bring back what they replaced.
 */

/**
 * How long a read is served from storage: the island's `staleTime`, so a
 * restored read is never refetched on mount and none outlives it.
 */
export const MINI_CART_CACHE_TTL_MS = CART_COUNT_STALE_TIME_MS;

/** Bump when what is stored changes shape; older entries are then dropped. */
export const MINI_CART_CACHE_VERSION = 1;

const KEY_PREFIX = 'compass:mini-cart-cache:';

/** The last invalidation of every identity, as a timestamp. */
const FENCE_KEY = 'compass:mini-cart-fence';

/**
 * How long a fence outlives the change it records: a read that started
 * before it may still land a request timeout later, and then stays fresh for
 * the TTL.
 */
const FENCE_LIFETIME_MS = 2 * MINI_CART_CACHE_TTL_MS;

export type MiniCartCacheIdentity = {
  userId: string;
  organizationId: string;
};

export type MiniCartCachedQuery = {
  queryKey: QueryKey;
  data: unknown;
  /** When the server sent `data`; the TTL runs from here. */
  dataUpdatedAt: number;
  /** When the read that produced `data` started, held against the fence. */
  validSince: number;
};

type MiniCartCacheEntry = MiniCartCacheIdentity & {
  version: number;
  /** The last invalidation; `0` for none. */
  clearedAt: number;
  queries: MiniCartCachedQuery[];
};

export const miniCartCacheKey = ({
  userId,
  organizationId,
}: MiniCartCacheIdentity): string =>
  `${KEY_PREFIX}${MINI_CART_CACHE_VERSION}:${encodeURIComponent(userId)}:${encodeURIComponent(organizationId)}`;

// Storage can be missing, full or blocked (private modes, policies): the
// mini-cart then reads as it did without a cache.
const storage = (): Storage | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const ownKeys = (store: Storage): string[] =>
  Array.from({ length: store.length }, (_, index) => store.key(index)).filter(
    (key): key is string => key?.startsWith(KEY_PREFIX) ?? false
  );

const parse = (raw: string | null): MiniCartCacheEntry | undefined => {
  try {
    const entry = raw ? (JSON.parse(raw) as MiniCartCacheEntry) : undefined;
    return entry?.version === MINI_CART_CACHE_VERSION &&
      Array.isArray(entry.queries)
      ? entry
      : undefined;
  } catch {
    return undefined;
  }
};

const readFence = (store: Storage): number =>
  Number(store.getItem(FENCE_KEY)) || 0;

const raiseFence = (store: Storage, now: number) => {
  const fence = String(Math.max(now, readFence(store)));
  try {
    store.setItem(FENCE_KEY, fence);
  } catch {
    // Unfenced, nothing stored can be trusted. Dropping it may free the room
    // the fence needs, and a read in flight would otherwise refill it.
    for (const key of ownKeys(store)) store.removeItem(key);
    try {
      store.setItem(FENCE_KEY, fence);
    } catch {
      // Still full: nothing is left to serve.
    }
  }
};

const isFresh = (query: MiniCartCachedQuery, now: number) =>
  now - query.dataUpdatedAt < MINI_CART_CACHE_TTL_MS;

const isLive = (entry: MiniCartCacheEntry, now: number) =>
  now - entry.clearedAt < FENCE_LIFETIME_MS ||
  entry.queries.some((query) => isFresh(query, now));

const save = (store: Storage, key: string, entry: MiniCartCacheEntry) => {
  try {
    store.setItem(key, JSON.stringify(entry));
  } catch {
    // Over quota: the previous entry may be stale, so it goes too.
    store.removeItem(key);
  }
};

/** Drops entries nothing can be served from or fenced by any more. */
const prune = (store: Storage, now: number) => {
  for (const key of ownKeys(store)) {
    const entry = parse(store.getItem(key));
    if (!entry || !isLive(entry, now)) store.removeItem(key);
  }
  if (now - readFence(store) >= FENCE_LIFETIME_MS) store.removeItem(FENCE_KEY);
};

/** The reads stored for `identity` that are still within the TTL. */
export const readMiniCartCache = (
  identity: MiniCartCacheIdentity,
  now = Date.now()
): MiniCartCachedQuery[] => {
  const store = storage();
  if (!store) return [];
  const fence = readFence(store);
  const entry = parse(store.getItem(miniCartCacheKey(identity)));
  if (
    entry?.userId !== identity.userId ||
    entry.organizationId !== identity.organizationId
  ) {
    return [];
  }
  return entry.queries.filter(
    (query) => query.validSince >= fence && isFresh(query, now)
  );
};

/**
 * Adds `queries` to what is stored for `identity`, a newer read replacing an
 * older one, and leaves out any read older than the fence. commerce-core
 * moves a user's one cart into the org it reads it for, so the user's
 * entries for other orgs are invalidated.
 */
export const writeMiniCartCache = (
  identity: MiniCartCacheIdentity,
  queries: MiniCartCachedQuery[],
  now = Date.now()
): void => {
  const store = storage();
  if (!store) return;
  const key = miniCartCacheKey(identity);
  const fence = readFence(store);
  const stored = parse(store.getItem(key));
  const clearedAt = Math.max(stored?.clearedAt ?? 0, fence);
  const incoming = queries.filter(
    (query) => query.validSince >= clearedAt && isFresh(query, now)
  );
  if (incoming.length === 0) return;
  const merged = new Map(
    (stored?.queries ?? [])
      .filter((query) => query.validSince >= clearedAt && isFresh(query, now))
      .map((query) => [JSON.stringify(query.queryKey), query])
  );
  for (const query of incoming) {
    const queryKey = JSON.stringify(query.queryKey);
    if (query.dataUpdatedAt >= (merged.get(queryKey)?.dataUpdatedAt ?? 0)) {
      merged.set(queryKey, query);
    }
  }
  for (const otherKey of ownKeys(store)) {
    const other = parse(store.getItem(otherKey));
    if (otherKey !== key && other?.userId === identity.userId) {
      save(store, otherKey, { ...other, clearedAt: now, queries: [] });
    }
  }
  prune(store, now);
  save(store, key, {
    version: MINI_CART_CACHE_VERSION,
    ...identity,
    clearedAt: stored?.clearedAt ?? 0,
    queries: [...merged.values()],
  });
};

/**
 * Empties every stored entry and fences it, plus `identity`'s if it has
 * none, and raises the fence of every identity, so no read in flight in any
 * tab can store what the change replaced. Storage is shared, so this reaches
 * every tab at once.
 */
export const invalidateMiniCartCache = (
  identity?: MiniCartCacheIdentity,
  now = Date.now()
): void => {
  const store = storage();
  if (!store) return;
  raiseFence(store, now);
  const entries = new Map<string, MiniCartCacheIdentity>();
  if (identity) entries.set(miniCartCacheKey(identity), identity);
  for (const key of ownKeys(store)) {
    const entry = parse(store.getItem(key));
    if (entry) entries.set(key, entry);
    else store.removeItem(key);
  }
  for (const [key, { userId, organizationId }] of entries) {
    save(store, key, {
      version: MINI_CART_CACHE_VERSION,
      userId,
      organizationId,
      clearedAt: now,
      queries: [],
    });
  }
};

/**
 * Removes every entry: sign-in and sign-out. The fence of every identity is
 * raised rather than removed, so a read in flight cannot store what was
 * there. Touches nothing when nothing is stored, so it is a no-op wherever
 * the mini-cart never ran (MULTI_CART off).
 */
export const clearMiniCartCache = (now = Date.now()): void => {
  const store = storage();
  if (!store) return;
  const keys = ownKeys(store);
  if (keys.length === 0 && store.getItem(FENCE_KEY) === null) return;
  for (const key of keys) store.removeItem(key);
  raiseFence(store, now);
};
